import {
  Modal,
  Button,
  List,
  Input,
  Spin,
  Avatar,
  Checkbox,
  Tooltip,
  Card,
} from 'antd';
import {
  AudioOutlined,
  FileTextOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import moment from 'moment';
import { Resizable } from 're-resizable';
import { fetchLastEhrAppointments, changeAppointmentStatus } from './service';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import { logger } from 'src/services';
import { analytics } from 'src/services';

// Helper function to strip HTML, get preview text, and insert newlines
const getNotePreview = (htmlContent, maxLength = 50, lineLength = 40) => {
  if (!htmlContent) return 'No notes available';

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  // Get text content and clean up whitespace
  let text = tempDiv.textContent || tempDiv.innerText || '';
  // text = text.replace(/\s+/g, ' ').trim(); // Replace multiple spaces/newlines with single space

  let previewText = text;
  if (text.length > maxLength) {
    // Truncate text first
    previewText = text.substring(0, maxLength) + '...';
  } else if (!text) {
    previewText = 'No text content';
  }
  // Handle the case where the original text was empty or just whitespace
  return previewText; // Return formatted text or original preview if formatting resulted in empty string
};

const EncounterSelectionModal = ({
  isOpen,
  onClose,
  patientId,
  practitionerId,
  patientName,
  practitionerName,
  encounterId,
  parent_appointment_ehr_id,
  appointmentId,
}) => {
  const [selectedAppointmentIds, setSelectedAppointmentIds] = useState([]);
  const [viewedAppointmentId, setViewedAppointmentId] = useState(null);
  const [appointmentChartDetails, setAppointmentChartDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [clinicalNotes, setClinicalNotes] = useState([]);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false);

  // New state for parsed JSON content and selected card keys
  const [parsedChartDetails, setParsedChartDetails] = useState(null);
  const [selectedCardKeys, setSelectedCardKeys] = useState([]);

  // const appointments = placeholderAppointments;

  useEffect(() => {
    if (isOpen && patientId && practitionerId) {
      const loadAppointments = async () => {
        setIsLoadingAppointments(true);
        setViewedAppointmentId(null);
        setAppointmentChartDetails(null);
        setParsedChartDetails(null); // Reset parsed details
        setSelectedCardKeys([]); // Reset selected keys
        setSelectedAppointmentIds([]);
        try {
          const data = await fetchLastEhrAppointments({
            patientId,
            practitionerId,
            appointmentId,
          });
          if (data && data.appointments) {
            const sortedAppointments = data.appointments.sort((a, b) =>
              moment(b.start_date).diff(moment(a.start_date))
            );

            // Augment appointments with reason_for_visit from clinical notes
            const clinicalNotesData = data.clinical_notes || [];
            const augmentedAppointments = sortedAppointments.map((app) => {
              const note = clinicalNotesData.find(
                (n) => n.appointment_id === app.appointment_id
              );
              if (
                note &&
                note.section &&
                note.section.encounter_reason_for_visit
              ) {
                return {
                  ...app,
                  reason_for_visit: note.section.encounter_reason_for_visit,
                };
              }
              return app;
            });

            setAppointments(augmentedAppointments || []);
            setClinicalNotes(clinicalNotesData);
            if (sortedAppointments && sortedAppointments.length > 0) {
              setViewedAppointmentId(sortedAppointments[0].appointment_id); // Set default view
              setSelectedAppointmentIds([sortedAppointments[0].appointment_id]); // Set default selection
            } else {
              setViewedAppointmentId(null);
              setSelectedAppointmentIds([]);
            }
          } else {
            setAppointments([]);
            setClinicalNotes([]);
            showToastError('No appointments found or unexpected data format.');
          }
        } catch (error) {
          showToastError(
            error.message || 'Failed to fetch recent appointments.'
          );
          setAppointments([]);
          setClinicalNotes([]);
        } finally {
          setIsLoadingAppointments(false);
        }
      };
      loadAppointments();
    } else if (!isOpen) {
      setAppointments([]);
      setClinicalNotes([]);
      setViewedAppointmentId(null);
      setAppointmentChartDetails(null);
      setParsedChartDetails(null); // Reset parsed details
      setSelectedCardKeys([]); // Reset selected keys
      setSelectedAppointmentIds([]);
      setIsLoadingAppointments(false);
      setIsLoadingDetails(false);
    }
  }, [isOpen, patientId, practitionerId]);

  useEffect(() => {
    if (viewedAppointmentId === null) {
      setAppointmentChartDetails(null);
      setParsedChartDetails(null);
      setSelectedCardKeys([]);
      setIsLoadingDetails(false);
      return;
    }

    setIsLoadingDetails(true);
    setParsedChartDetails(null); // Reset before loading new details
    setSelectedCardKeys([]);
    setAppointmentChartDetails(null);

    // Find the clinical note corresponding to the viewed appointment ID
    const note = clinicalNotes.find(
      (n) => n.appointment_id === viewedAppointmentId
    );

    if (note?.content) {
      try {
        const parsed = JSON.parse(note.content);
        // Ensure parsed is a non-null, non-array object with at least one key
        if (
          parsed &&
          typeof parsed === 'object' &&
          !Array.isArray(parsed) &&
          Object.keys(parsed).length > 0
        ) {
          setParsedChartDetails(parsed);
          setSelectedCardKeys(Object.keys(parsed)); // Preselect all cards
          setAppointmentChartDetails(null); // Clear any old HTML view
        } else {
          // Content is not a suitable JSON object (e.g. empty object, array, primitive, or not JSON at all)
          setParsedChartDetails(null);
          setSelectedCardKeys([]);
          setAppointmentChartDetails(
            note.content ||
              '<p class="text-gray-500">No clinical notes available or content is not in the expected JSON format.</p>'
          );
        }
      } catch (e) {
        // JSON parsing failed, treat as plain HTML or text
        setParsedChartDetails(null);
        setSelectedCardKeys([]);
        setAppointmentChartDetails(
          note.content ||
            '<p class="text-gray-500">Error parsing clinical notes. Displaying as raw text.</p>'
        );
      }
    } else {
      // Handle case where no note is found or content is empty
      setParsedChartDetails(null);
      setSelectedCardKeys([]);
      setAppointmentChartDetails(
        '<p class="text-gray-500">No clinical notes available for this appointment.</p>'
      );
    }

    setIsLoadingDetails(false); // Set loading to false after processing
  }, [viewedAppointmentId, clinicalNotes]); // Add clinicalNotes to dependency array

  const handleCardSelectionToggle = (key) => {
    setSelectedCardKeys((prevKeys) =>
      prevKeys.includes(key)
        ? prevKeys.filter((k) => k !== key)
        : [...prevKeys, key]
    );
  };

  const handleConfirm = async () => {
    let notesToSubmit = null;
    if (parsedChartDetails && selectedCardKeys.length > 0) {
      const selectedContent = {};
      selectedCardKeys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(parsedChartDetails, key)) {
          selectedContent[key] = parsedChartDetails[key];
        }
      });
      notesToSubmit = JSON.stringify(selectedContent); // Send selected parts as a JSON string
    }

    try {
      await changeAppointmentStatus({
        appointmentIds: selectedAppointmentIds,
        status: 'precharted',
        parent_appointment_ehr_id: parent_appointment_ehr_id,
        practitioner_id: practitionerId,
        encounter_id: encounterId,
        notes_content: notesToSubmit, // Assumed field name for the selected notes
      });
      analytics.track('Successfully linked encounter history.', {
        encounter_id: encounterId,
      });
      showToastSuccess('Successfully linked encounter history.');
      setViewedAppointmentId(null);
      // Resetting states, though setViewedAppointmentId(null) should trigger useEffect to clear most
      setParsedChartDetails(null);
      setSelectedCardKeys([]);
      onClose();
    } catch (error) {
      showToastError(error.message || 'Failed to change appointment status.');
    }
  };

  const handleCancel = () => {
    setViewedAppointmentId(null);
    onClose();
  };

  return (
    <Modal
      title="Select Encounter to Link History"
      open={isOpen}
      onCancel={handleCancel}
      style={{ width: '190%', maxWidth: '1400px' }}
      footer={[
        <Button
          key="cancel"
          onClick={handleCancel}
          style={{ marginRight: 'auto' }}
        >
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          className={` ${selectedCardKeys.length > 0 ? 'bg-green-500 border-green-500 hover:bg-green-600 hover:border-green-600 text-white' : 'bg-gray-300 border-gray-300 text-gray-500 cursor-not-allowed'}`}
          onClick={handleConfirm}
          disabled={selectedCardKeys.length === 0}
        >
          Add selected sections
        </Button>,
      ]}
      width={1000}
    >
      <p className="mb-4">
        Please select the encounter you want to associate this history with:
      </p>
      {isLoadingAppointments ? (
        <div className="text-center py-10">
          <Spin size="large" />
          <p>Loading past appointments...</p>
        </div>
      ) : (
        <div
          className="flex flex-col"
          style={{ maxHeight: '65vh', width: '100%' }}
        >
          {/* Top Panel for Appointments List */}
          <div className="w-full bg-white border border-gray-200 border-b-0 rounded-t-lg rounded-b-none p-4">
            {appointments.length === 0 ? (
              <p className="text-gray-500 text-center">
                No past appointments found.
              </p>
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={appointments} // Use fetched appointments
                renderItem={(item) => {
                  const isSelected = selectedAppointmentIds.includes(
                    item.appointment_id
                  );
                  const isViewing = viewedAppointmentId === item.appointment_id;
                  // Basic status mapping (adjust as needed based on actual status IDs)

                  return (
                    <List.Item
                      actions={[]}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: isSelected
                          ? '#f3f4f6' // Changed from '#eef2ff' to a richer indigo (Tailwind indigo-200)
                          : isViewing
                            ? '#f3f4f6' // Light gray for viewing
                            : 'transparent',
                        // borderLeft: isViewing ? '4px solid #4f46e5' : 'none', // Indigo border for viewing
                        paddingLeft: isViewing ? '12px' : '16px', // Adjust padding for border
                        transition:
                          'background-color 0.2s ease, border-left 0.2s ease', // Smooth transition
                      }}
                      onClick={() => {
                        setViewedAppointmentId(item.appointment_id);
                        setSelectedAppointmentIds([item.appointment_id]); // Also update selected IDs
                      }}
                    >
                      <List.Item.Meta
                        className="items-center"
                        avatar={
                          <Avatar
                            size="large"
                            style={{
                              backgroundColor: '#d1d5db',
                              color: '#1f2937',
                            }}
                          >
                            {/* Use patientName prop or initials from it */}
                            {patientName
                              ?.split(' ')
                              .map((n) => n[0])
                              .slice(0, 2)
                              .join('') || '??'}
                          </Avatar>
                        }
                        title={
                          <span className="font-semibold">
                            {patientName ||
                              `Appointment ID: ${item.appointment_id}`}
                          </span>
                        }
                        description={
                          <>
                            <div className="text-gray-600 flex items-center">
                              <AudioOutlined style={{ marginRight: '4px' }} />{' '}
                              {practitionerName ||
                                `Practitioner ID: ${item.practitioner_id}`}
                            </div>

                            {/* Container for Date, Time, and Reason for Visit */}
                            <div
                              style={{ marginTop: '8px', marginLeft: '-40px' }}
                            >
                              <div className="text-gray-600 flex items-center text-sm">
                                <CalendarOutlined
                                  style={{ marginRight: '8px' }} // Increased margin for visual separation
                                />
                                {moment(item.start_date).format('MMM Do, YYYY')}
                              </div>
                              <div
                                className="text-gray-600 flex items-center text-sm"
                                style={{ marginTop: '4px' }}
                              >
                                <ClockCircleOutlined
                                  style={{ marginRight: '8px' }} // Increased margin
                                />
                                {moment(item.start_date).format('hh:mm A')}
                              </div>

                              {item.reason_for_visit && (
                                <div style={{ marginTop: '8px' }}>
                                  <div
                                    className="text-xs text-gray-500"
                                    style={{ marginBottom: '2px' }}
                                  >
                                    Reason For Visit:
                                  </div>
                                  <div
                                    className="text-sm text-gray-800" // Darker text for the reason itself
                                    style={{
                                      padding: '4px 8px',
                                      borderLeft: '4px solid #FBBF24', // amber-400
                                      backgroundColor: '#FEF9C3', // amber-100 for light yellow background
                                      whiteSpace: 'pre-wrap',
                                    }}
                                  >
                                    {item.reason_for_visit}
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            )}
          </div>

          {/* Bottom Panel for Details (Cards) */}
          <div className="flex-1 overflow-y-auto bg-white border border-gray-200 border-t-0 rounded-b-lg rounded-t-none p-4">
            {isLoadingDetails ? (
              <div className="text-center py-10">
                <Spin size="large" />
                <p>Loading details...</p>
              </div>
            ) : viewedAppointmentId ? (
              parsedChartDetails &&
              Object.keys(parsedChartDetails).length > 0 ? (
                <div>
                  {/* Select All Checkbox and Counter */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px',
                      paddingBottom: '8px',
                      borderBottom: '1px solid #e5e7eb',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Checkbox
                      checked={
                        parsedChartDetails &&
                        selectedCardKeys.length ===
                          Object.keys(parsedChartDetails).length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCardKeys(Object.keys(parsedChartDetails));
                        } else {
                          setSelectedCardKeys([]);
                        }
                      }}
                    >
                      Select All Sections
                    </Checkbox>
                    <span className="text-sm text-gray-500">
                      {Object.keys(parsedChartDetails).length} section
                      {Object.keys(parsedChartDetails).length !== 1
                        ? 's'
                        : ''}{' '}
                      available
                    </span>
                  </div>

                  {Object.entries(parsedChartDetails).map(([key, value]) => (
                    <Card
                      key={key}
                      title={key}
                      size="small"
                      style={{
                        marginBottom: 12,
                        ...(selectedCardKeys.includes(key) && {
                          backgroundColor: '#f0fdf4',
                        }), // Light green for selected CARDS
                      }}
                      extra={
                        <Checkbox
                          checked={selectedCardKeys.includes(key)}
                          onChange={() => handleCardSelectionToggle(key)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                      bodyStyle={{ paddingTop: 8, paddingBottom: 8 }}
                    >
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {typeof value === 'string'
                          ? value
                          : JSON.stringify(value)}
                      </div>
                    </Card>
                  ))}
                </div>
              ) : appointmentChartDetails ? (
                <div
                  dangerouslySetInnerHTML={{ __html: appointmentChartDetails }}
                />
              ) : (
                <p className="text-gray-500 text-center h-full flex items-center justify-center">
                  Details not available in the expected format or no details to
                  display.
                </p>
              )
            ) : (
              <p className="text-gray-500 text-center h-full flex items-center justify-center">
                {appointments.length > 0
                  ? 'Select an appointment on the left to view clinical notes.'
                  : 'No appointments loaded.'}
              </p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

EncounterSelectionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  practitionerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  patientName: PropTypes.string,
  practitionerName: PropTypes.string,
  encounterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parent_appointment_ehr_id: PropTypes.string,
  appointmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default EncounterSelectionModal;
